import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calculator, CheckCircle2, XCircle, Loader2, BookOpen } from "lucide-react";

// Einstellungen → Website-Anbindung: Website-URL für die CRM-Kalkulation + Verbindungstest.
export function KalkulationSettings({ isAdmin }) {
  const [url, setUrl] = useState("");
  const [saved, setSaved] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [doc, setDoc] = useState(null);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => { api.get("/kalkulation/einstellungen").then((r) => { setUrl(r.data.site_url); setSaved(r.data.site_url); setCanEdit(!!r.data.can_edit); }).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/kalkulation/einstellungen", { site_url: url }); setUrl(r.data.site_url); setSaved(r.data.site_url); toast.success("Website-URL gespeichert."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true); setResult(null);
    try { const r = await api.post("/kalkulation/test"); setResult(r.data); }
    catch (e) { setResult({ ok: false, message: e?.response?.data?.detail || "Test fehlgeschlagen." }); }
    finally { setTesting(false); }
  };
  const openDoc = async () => {
    try { const r = await api.get("/kalkulation/doku", { responseType: "text" }); setDoc(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Dokument konnte nicht geladen werden."); }
  };

  return (
    <div className="space-y-3 border-t border-slate-100 pt-4" data-testid="kalk-settings">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Calculator className="w-4 h-4" /> Kalkulation über die Website</div>
      <p className="text-xs text-slate-500">Dateien in Aufträgen werden mit dem Website-Kalkulator berechnet. Authentifizierung über das Ingest-Token oben (Header X-CRM-Token).</p>
      <div>
        <Label htmlFor="kalk-site-url">Website-URL für Kalkulation</Label>
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          <Input id="kalk-site-url" value={url} readOnly={!canEdit} onChange={(e) => setUrl(e.target.value)} placeholder="https://druckexperten.com" className="font-mono text-xs" data-testid="kalk-site-url-input" />
          {canEdit && <Button variant="outline" onClick={save} disabled={saving || url === saved} data-testid="kalk-site-url-save">{saving ? "Speichert…" : "Speichern"}</Button>}
          <Button variant="outline" onClick={test} disabled={testing || url !== saved} title={url !== saved ? "Bitte zuerst speichern" : ""} data-testid="kalk-test-button">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Verbindung testen
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1" data-testid="kalk-site-url-hint">
          {canEdit ? "Nur https mit öffentlichem Domainnamen. Die Adresse erhält das Ingest-Token – nur Administratoren können sie ändern." : "Nur Administratoren können die Adresse ändern (sie erhält das Ingest-Token)."}
        </p>
      </div>
      {result && (
        <div className={`text-sm p-3 rounded-md border flex items-start gap-2 ${result.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`} data-testid="kalk-test-result">
          {result.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
          <span>{result.message}</span>
        </div>
      )}
      {isAdmin && (
        <button type="button" onClick={openDoc} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded" data-testid="kalk-doc-link">
          <BookOpen className="w-3.5 h-3.5" /> Schnittstellen-Beschreibung (Plugin)
        </button>
      )}
      <Dialog open={doc !== null} onOpenChange={(o) => !o && setDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="kalk-doc-dialog">
          <DialogHeader><DialogTitle>Schnittstellen-Beschreibung (Plugin)</DialogTitle></DialogHeader>
          <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(doc || ""); toast.success("Kopiert."); }} data-testid="kalk-doc-copy">Kopieren</Button></div>
          <pre className="text-xs whitespace-pre-wrap font-mono bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="kalk-doc-content">{doc}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
